import React, { useState } from 'react'
import { Alert, Box, Tab, Table, TableBody, TableCell, TableRow } from '@mui/material'
import Typography from '@mui/material/Typography'
import InputField from '../../../../../../FormControl/input/InputField'
import DialogConfirm from '../../../../../../dialog/DialogConfirm'
import useAnalyticsCurriculum from '../../../../../../../hooks/analytics/useAnalyticsCurriculum'
import InputColor from '../../../../../../FormControl/input/InputColor'
import ButtonStartIcon from '../../../../../../button/ButtonStartIcon'
import ItemRule from './ItemRule'
import IconTooltipButton from '../../../../../../button/IconTooltipButton'
import FormRule from './FormRule'

const ResultsDialog = ({ show, onClose, type, formData = {}, onReset }) => {
  const types = ['create', 'edit', 'delete', 'preview']
  const { createModule, updateModule, deleteModule } = useAnalyticsCurriculum()
  const [form, setInputForm] = useState(formData)
  const [errors, setInputErrors] = useState(null)
  const [loading, toggleLoading] = useState(false)
  const [modal, setModal] = useState(null)

  const onChange = (name, value) => {
    setInputForm({ ...form, [name]: value })
    setInputErrors({ ...errors, [name]: null })
  }

  const handleSaveRules = (newRules) => {
    onChange('rules', newRules)
  }

  const onSave = async (e) => {
    if (!types.includes(type)) {
      setInputErrors({ message: 'Invalid type' })
      return
    }
    e.preventDefault()
    toggleLoading(true)
    try {
      let result = { error: false }
      if (type === 'create') {
        result = await createModule('results', form)
      }
      if (type === 'edit') {
        result = await updateModule('results', form._id, form)
      }
      if (type === 'delete') {
        result = await deleteModule('results', form._id)
      }
      if (result.error) {
        setInputErrors(result)
      } else {
        onClose()
        onReset()
      }
    } finally {
      toggleLoading(false)
    }
  }

  const onAddRule = () => {
    const modelCondition = {
      id: Date.now(),
      condition: '',
      includesLevelId: []
    }
    const modelRule = {
      description: '',
      statusId: [],
      conditions: [modelCondition]
    }
    const newRules = form.rules ? [...form.rules, modelRule] : [modelRule]
    setInputForm({ ...form, rules: newRules })
  }

  const onEditRule = (index, updatedRule) => {
    const newRules = [...form.rules]
    newRules[index] = updatedRule
    setInputForm({ ...form, rules: newRules })
  }

  const onDeleteRule = (index) => {
    const newRules = form.rules.filter((_, i) => i !== index)
    setInputForm({ ...form, rules: newRules })
  }

  return (
    <DialogConfirm
      id={`${type}-results`}
      open={show}
      title={`${type} results`}
      onCancel={onClose}
      onConfirm={['create', 'edit', 'delete'].includes(type) ? onSave : undefined}
      textConfirm={type === 'delete' ? 'Confirm' : 'Save'}
      onCloseHeader={type === 'preview'}
      showActions={['create', 'edit', 'delete'].includes(type)}
      loading={loading}
      maxWidth='xs'
    >
      {types.includes(type) ? (
        <>
          {type === 'delete' ? (
            <Typography component={'div'}>
              Are you sure you want to delete the result: <b>{form.name}</b>?
            </Typography>
          ) : (
            <div className='flex flex-col gap-2 py-4'>
              <div className='grid grid-cols-4 items-start gap-4'>
                <Typography
                  variant='inherit'
                  className='font-bold'
                  color='primary'
                  align='right'
                  component='p'
                >
                  Priority
                </Typography>
                {type === 'preview' ? (
                  <span>{form.priority}</span>
                ) : (
                  <InputField
                    id='priority'
                    name='priority'
                    value={form.priority}
                    error={errors?.priority}
                    helperText={errors?.priority}
                    onChange={(e) => onChange('priority', e.target.value)}
                    fullWidth
                    type='number'
                  />
                )}
                <Typography
                  variant='inherit'
                  className='font-bold'
                  color='primary'
                  align='right'
                  component='p'
                >
                  Position
                </Typography>
                {type === 'preview' ? (
                  <span>{form.position}</span>
                ) : (
                  <InputField
                    id='position'
                    name='position'
                    value={form.position}
                    error={errors?.position}
                    helperText={errors?.position}
                    onChange={(e) => onChange('position', e.target.value)}
                    fullWidth
                    type='number'
                  />
                )}
                <Typography
                  variant='inherit'
                  className='font-bold'
                  color='primary'
                  align='right'
                  component='p'
                >
                  Code
                </Typography>
                <div className='col-span-3'>
                  {['preview', 'edit'].includes(type) ? (
                    <Typography>{form.code}</Typography>
                  ) : (
                    <InputField
                      id='code'
                      name='code'
                      value={form.code}
                      error={errors?.code}
                      helperText={errors?.code}
                      onChange={(e) => onChange('code', e.target.value)}
                      fullWidth
                    />
                  )}
                </div>
                <Typography
                  variant='inherit'
                  className='font-bold'
                  color='primary'
                  align='right'
                  component='p'
                >
                  Name
                </Typography>
                <div className='col-span-3'>
                  {type === 'preview' ? (
                    <Typography>{form.name}</Typography>
                  ) : (
                    <InputField
                      id='name'
                      name='name'
                      value={form.name}
                      error={errors?.name}
                      helperText={errors?.name}
                      onChange={(e) => onChange('name', e.target.value)}
                      fullWidth
                    />
                  )}
                </div>
                <Typography
                  variant='inherit'
                  className='font-bold'
                  color='primary'
                  align='right'
                  component='p'
                >
                  Description
                </Typography>
                <div className='col-span-3'>
                  {type === 'preview' ? (
                    <Typography>{form.description}</Typography>
                  ) : (
                    <InputField
                      id='description'
                      name='description'
                      value={form.description}
                      error={errors?.description}
                      helperText={errors?.description}
                      onChange={(e) => onChange('description', e.target.value)}
                      fullWidth
                      multiline={true}
                    />
                  )}
                </div>
                <Typography
                  variant='inherit'
                  className='font-bold'
                  color='primary'
                  align='right'
                  component='p'
                >
                  Text Color
                </Typography>
                <div className='col-span-3'>
                  <InputColor
                    id='text-color'
                    name='color'
                    value={form.color}
                    error={errors?.color}
                    helperText={errors?.color}
                    onChange={(value) => onChange('color', value)}
                    fullWidth
                    disabled={type === 'preview'}
                  />
                </div>
                <Typography
                  variant='inherit'
                  className='font-bold'
                  color='primary'
                  align='right'
                  component='p'
                >
                  Background
                </Typography>
                <div className='col-span-3'>
                  <InputColor
                    id='bg-color'
                    name='bgColor'
                    value={form.bgColor}
                    error={errors?.bgColor}
                    helperText={errors?.bgColor}
                    onChange={(value) => onChange('bgColor', value)}
                    fullWidth
                    disabled={type === 'preview'}
                  />
                </div>
                <Typography
                  variant='inherit'
                  className='font-bold'
                  color='primary'
                  align='right'
                  component='p'
                >
                  Border
                </Typography>
                <div className='col-span-3'>
                  <InputColor
                    id='border'
                    name='border'
                    value={form.border}
                    error={errors?.border}
                    helperText={errors?.border}
                    onChange={(value) => onChange('border', value)}
                    fullWidth
                    disabled={type === 'preview'}
                  />
                </div>
              </div>
              <Box className='bg-primary text-primary-foreground px-4 py-2 flex gap-4 items-center justify-between sticky top-0 z-10 h-8'>
                <Typography variant='subtitle1' className='uppercase'>
                  Rules
                </Typography>
                {type !== 'preview' && (
                  <ButtonStartIcon
                    onClick={onAddRule}
                    color='secondary'
                    variant='contained'
                    startIcon={['fas', 'plus']}
                  >
                    Add rule
                  </ButtonStartIcon>
                )}
              </Box>
              <Box className='flex flex-col gap-2 py-4 px-4'>
                {form.rules?.map((rule, index) => (
                  <Box key={rule._id || index} className='border border-b-2 border-primary'>
                    <FormRule
                      formData={rule}
                      onEditRule={(updatedRule) => onEditRule(index, updatedRule)}
                      onDeleteRule={() => onDeleteRule(index)}
                      isPreview={type === 'preview'}
                    />
                  </Box>
                ))}
              </Box>
            </div>
          )}
        </>
      ) : (
        <Alert severity='error'>Invalid type</Alert>
      )}
      {errors && errors.message && (
        <Alert severity='error' onClose={() => setInputErrors({ message: null })}>
          {errors.message}
        </Alert>
      )}
    </DialogConfirm>
  )
}

export default ResultsDialog
